import React from 'react';
import { Card } from './card';
import { Label } from './label';
import { Switch } from './switch';
import { Slider } from './slider';
import { ArrowLeft, ArrowRight, Zap, Maximize2 } from 'lucide-react';

interface CarouselSettings {
  speed: number;
  direction: 'left' | 'right';
  focusCardSize: number;
  normalCardSize: number;
}

interface CarouselSettingsProps {
  settings: CarouselSettings;
  onSettingsChange: (settings: Partial<CarouselSettings>) => void;
  title: string;
  className?: string;
}

export const CarouselSettings: React.FC<CarouselSettingsProps> = ({
  settings,
  onSettingsChange,
  title,
  className = ''
}) => {
  // Provide default settings if settings is undefined
  const safeSettings = settings || {
    speed: 50,
    direction: 'right' as const,
    focusCardSize: 120,
    normalCardSize: 80
  };
  const handleSpeedChange = (value: number[]) => {
    onSettingsChange({ speed: value[0] });
  };

  const handleDirectionChange = (checked: boolean) => {
    onSettingsChange({ direction: checked ? 'left' : 'right' });
  };

  const handleFocusSizeChange = (value: number[]) => {
    onSettingsChange({ focusCardSize: value[0] });
  };

  const handleNormalSizeChange = (value: number[]) => {
    onSettingsChange({ normalCardSize: value[0] });
  };

  return (
    <Card className={`p-6 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      
      <div className="space-y-6">
        {/* Speed Control */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-gray-600" />
            <Label className="text-sm font-medium">Scroll Speed</Label>
            <span className="text-sm text-gray-500 ml-auto">{safeSettings.speed} px/s</span>
          </div>
          <Slider
            value={[safeSettings.speed]}
            onValueChange={handleSpeedChange}
            max={200}
            min={10}
            step={5}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>Slow (10 px/s)</span>
            <span>Fast (200 px/s)</span>
          </div>
        </div>

        {/* Direction Control */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {safeSettings.direction === 'right' ? (
                <ArrowRight className="w-4 h-4 text-gray-600" />
              ) : (
                <ArrowLeft className="w-4 h-4 text-gray-600" />
              )}
              <Label className="text-sm font-medium">Direction</Label>
            </div>
            <Switch
              checked={safeSettings.direction === 'left'}
              onCheckedChange={handleDirectionChange}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>Right to Left</span>
            <span>Left to Right</span>
          </div>
        </div>

        {/* Focus Card Size Control */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Maximize2 className="w-4 h-4 text-gray-600" />
            <Label className="text-sm font-medium">Focus Card Size</Label>
            <span className="text-sm text-gray-500 ml-auto">{safeSettings.focusCardSize}%</span>
          </div>
          <Slider
            value={[safeSettings.focusCardSize]}
            onValueChange={handleFocusSizeChange}
            max={200}
            min={100}
            step={5}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>Normal (100%)</span>
            <span>Large (200%)</span>
          </div>
        </div>

        {/* Normal Card Size Control */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Maximize2 className="w-4 h-4 text-gray-600" />
            <Label className="text-sm font-medium">Normal Card Size</Label>
            <span className="text-sm text-gray-500 ml-auto">{safeSettings.normalCardSize}%</span>
          </div>
          <Slider
            value={[safeSettings.normalCardSize]}
            onValueChange={handleNormalSizeChange}
            max={100}
            min={50}
            step={5}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>Small (50%)</span>
            <span>Normal (100%)</span>
          </div>
        </div>

        {/* Preview */}
        <div className="pt-4 border-t border-gray-200">
          <Label className="text-sm font-medium text-gray-700 mb-2 block">Preview</Label>
          <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
            <div 
              className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center text-xs text-gray-600 transition-transform duration-300"
              style={{ transform: `scale(${safeSettings.normalCardSize / 100})` }}
            >
              Normal
            </div>
            <div 
              className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-xs text-white transition-transform duration-300"
              style={{ transform: `scale(${safeSettings.focusCardSize / 100})` }}
            >
              Focus
            </div>
            <div 
              className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center text-xs text-gray-600 transition-transform duration-300"
              style={{ transform: `scale(${safeSettings.normalCardSize / 100})` }}
            >
              Normal
            </div>
            <div className="text-xs text-gray-500 ml-2">
              Normal: {safeSettings.normalCardSize}%, Focus: {safeSettings.focusCardSize}%
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};
